import { useState } from 'react';
import { Plus, Scan } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useWalletSystem } from '@/hooks/useWalletSystem';
import { useCustomers } from '@/hooks/useCustomers';
import { useUser } from '@/contexts/UserContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Html5Qrcode } from 'html5-qrcode';
import { format } from 'date-fns';

export default function WalletAdmin() {
  const { user, isAdmin } = useUser();
  const { t } = useLanguage();
  const { allWallets, addCredit, isAddingCredit } = useWalletSystem();
  const { customers } = useCustomers();

  const [showAddCredit, setShowAddCredit] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);

  const [creditData, setCreditData] = useState({
    customerCode: '',
    currency: 'USD',
    amount: '',
    description: '',
  });

  // Find customer by code
  const selectedCustomer = customers.find(c => c.code === creditData.customerCode);

  // Start QR scanner
  const startScanner = async () => {
    setScannerActive(true);
    const html5QrCode = new Html5Qrcode("qr-reader-admin");
    
    try {
      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        (decodedText) => {
          // Scanned QR code - it should contain customer code
          setCreditData(prev => ({ ...prev, customerCode: decodedText }));
          html5QrCode.stop();
          setScannerActive(false);
          setShowScanner(false);
          setShowAddCredit(true);
        },
        () => {
          // Ignore errors
        }
      );
    } catch (err) {
      console.error("Error starting scanner:", err);
      setScannerActive(false);
    }
  };

  // Handle add credit
  const handleAddCredit = () => {
    addCredit({
      customerCode: creditData.customerCode,
      currency: creditData.currency,
      amount: parseFloat(creditData.amount),
      description: creditData.description || 'Cash deposit',
    }, {
      onSuccess: () => {
        setShowAddCredit(false);
        setCreditData({
          customerCode: '',
          currency: 'USD',
          amount: '',
          description: '',
        });
      }
    });
  };

  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="py-12">
            <p className="text-center text-muted-foreground">
              You don't have permission to access this page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Wallet Management</h1>
        <div className="flex gap-2">
          <Button onClick={() => setShowScanner(true)} variant="outline">
            <Scan className="h-4 w-4 mr-2" />
            Scan QR
          </Button>
          <Button onClick={() => setShowAddCredit(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Credit
          </Button>
        </div>
      </div>

      {/* All Wallets Table */}
      <Card>
        <CardHeader>
          <CardTitle>Customer Wallets</CardTitle>
          <CardDescription>View and manage all customer wallets</CardDescription>
        </CardHeader>
        <CardContent>
          {allWallets && allWallets.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead className="text-right">Balance (USD)</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allWallets.map((wallet: any) => (
                  <TableRow key={wallet.id}>
                    <TableCell className="font-medium">{wallet.customer_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{wallet.customer_code}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-lg">
                      ${parseFloat(wallet.balance_usd || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={wallet.status === 'active' ? 'default' : 'destructive'}>
                        {wallet.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {wallet.updated_at ? format(new Date(wallet.updated_at), 'MMM dd, yyyy') : '-'}
                    </TableCell>
                    <TableCell>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setCreditData(prev => ({ ...prev, customerCode: wallet.customer_code }));
                          setShowAddCredit(true);
                        }}
                      >
                        Add Credit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No wallets found
            </p>
          )}
        </CardContent>
      </Card>

      {/* QR Scanner Dialog */}
      <Dialog open={showScanner} onOpenChange={(open) => {
        setShowScanner(open);
        if (!open && scannerActive) {
          const html5QrCode = new Html5Qrcode("qr-reader-admin");
          html5QrCode.stop().catch(() => {});
          setScannerActive(false);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Scan Customer QR Code</DialogTitle>
            <DialogDescription>
              Scan customer's wallet QR code to add credit
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div id="qr-reader-admin" className="w-full"></div>
            {!scannerActive && (
              <Button onClick={startScanner} className="w-full mt-4">
                Start Scanner
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Credit Dialog */}
      <Dialog open={showAddCredit} onOpenChange={setShowAddCredit}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Credit to Wallet</DialogTitle>
            <DialogDescription>
              Add cash deposit to customer's wallet
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Customer Code</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter customer code"
                  value={creditData.customerCode}
                  onChange={(e) => setCreditData(prev => ({ ...prev, customerCode: e.target.value }))}
                />
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowAddCredit(false);
                    setShowScanner(true);
                  }}
                >
                  <Scan className="h-4 w-4" />
                </Button>
              </div>
              {selectedCustomer && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="font-medium text-green-900">{selectedCustomer.name}</p>
                  <p className="text-sm text-green-700">Code: {selectedCustomer.code}</p>
                </div>
              )}
              {creditData.customerCode && !selectedCustomer && (
                <p className="text-sm text-red-600">Customer not found</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Amount (USD)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={creditData.amount}
                onChange={(e) => setCreditData(prev => ({ ...prev, amount: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="e.g., Cash deposit"
                value={creditData.description}
                onChange={(e) => setCreditData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <Button 
              onClick={handleAddCredit} 
              disabled={!selectedCustomer || !creditData.amount || isAddingCredit}
              className="w-full"
            >
              {isAddingCredit ? 'Processing...' : 'Add Credit'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
